import type { Block, Fabric, Project } from "./state"
import { FABRIC_WIDTH, findFabricByRole, findFabricBySlot } from "./state"
import { blockFinishedInches, schemaPieces, type SchemaPiece } from "./blockschema"
import { blockForSchema, groupGrid } from "./layout"

export type { SchemaPiece as CutPiece }
export { schemaPieces as blockPieces }

export interface CutBatch {
  fabric: string
  patchName: string
  size: number
  length: number
  count: number
}

export interface StripPlan {
  size: number
  count: number
  squaresPerStrip: number
  strips: number
  inchesUsed: number
}

export { blockFinishedInches }


export function cutBatches(project: Project): CutBatch[] {
  const groups = sortedGroups(project)
  if (groups.length === 0) return []

  // Walk the actual layout grid to count cells per (schemaName, group) pair.
  // This handles the primary schema, alternate_schema, and per-cell overrides
  // in one pass — each combination is counted exactly as it appears.
  const grid = groupGrid(project)
  const tally = new Map<string, number>()
  for (const row of grid) {
    for (const cell of row) {
      if (!cell.group) continue
      const key = `${cell.schemaName}\x00${cell.group}`
      tally.set(key, (tally.get(key) ?? 0) + 1)
    }
  }

  const batches: CutBatch[] = []
  for (const [key, count] of tally) {
    const sep = key.indexOf("\x00")
    const schemaName = key.slice(0, sep)
    const group = key.slice(sep + 1)
    const block = blockForSchema(project, schemaName)
    const pieces = schemaPieces(block)
    const pieceRoles = block.piece_roles
    for (const piece of pieces) {
      const fabricSlot = pieceRoles[piece.patchName] ?? piece.patchName
      const fabric = findFabricByGroupSlot(project, group, fabricSlot)
      if (!fabric) continue
      batches.push({
        fabric: fabric.name,
        patchName: piece.patchName,
        size: piece.size,
        length: piece.length,
        count: piece.count * count,
      })
    }
  }
  return batches
}

export function blocksTotal(project: Project): number {
  return project.layout.rows * project.layout.cols
}

// Returns the number of PRIMARY schema blocks per colorway group. When an
// alternate_schema is active, primary blocks occupy roughly half the cells;
// otherwise every cell is primary and behavior is unchanged.
export function blocksPerColorway(project: Project): number {
  const groups = sortedGroups(project)
  if (groups.length === 0) return 0
  const total = blocksTotal(project)
  if (project.layout.alternate_schema) {
    return Math.floor(Math.ceil(total / 2) / groups.length)
  }
  return Math.floor(total / groups.length)
}

// Returns the number of ALTERNATE schema blocks per colorway group, or 0 when
// no alternate schema is active.
export function alternateBlocksPerColorway(project: Project): number {
  if (!project.layout.alternate_schema) return 0
  const groups = sortedGroups(project)
  if (groups.length === 0) return 0
  return Math.floor(Math.floor(blocksTotal(project) / 2) / groups.length)
}

export function stripPlan(batch: CutBatch, fabricWidth = FABRIC_WIDTH): StripPlan {
  // Cross-cut length governs how many pieces fit on one WOF strip; strip
  // width governs how much WOF length each strip consumes. For square
  // pieces these are the same number (length === size), so behaviour is
  // unchanged; for non-square strip patches (Rail Fence) the long edge
  // shrinks pieces-per-strip while the short edge keeps inches-used honest.
  const piecesPerStrip = Math.max(1, Math.floor(fabricWidth / batch.length))
  const strips = Math.ceil(batch.count / piecesPerStrip)
  return {
    size: batch.size,
    count: batch.count,
    squaresPerStrip: piecesPerStrip,
    strips,
    inchesUsed: strips * batch.size,
  }
}

export function sortedGroups(project: Project): string[] {
  const groups = new Set<string>()
  for (const f of project.fabrics) if (f.group) groups.add(f.group)
  return [...groups].sort()
}

export function findFabricByGroupSlot(project: Project, group: string, slot: string): Fabric | null {
  return project.fabrics.find((f) => f.group === group && f.slot === slot) ?? null
}

export interface SashingStripPlan {
  sashCut: number          // WOF strip width (sashing_width + 2*seam)
  cornerCut: number        // cornerstone square size (= sashCut)
  sashCount: number        // total sashing strip pieces (vert + horiz)
  cornerCount: number      // cornerstone squares from this fabric
  piecesPerStrip: number   // legacy single value (= vertPiecesPerStrip for square blocks)
  sashStrips: number       // total WOF strips needed (vert + horiz)
  cornerStrips: number
  sashInches: number       // total WOF inches consumed by sashing (vert + horiz)
  cornerInches: number

  // Split fields. For square blocks vertPieceCut === horizPieceCut and the
  // legacy fields above stay accurate; renderers branch on inequality to
  // emit two rows (one for vertical between-column sashing, one for
  // horizontal between-row sashing) when the block is non-square.
  vertPieceCut: number     // length of strips between columns (= blockH + 2*seam)
  horizPieceCut: number    // length of strips between rows    (= blockW + 2*seam)
  vertCount: number
  horizCount: number
  vertPiecesPerStrip: number
  horizPiecesPerStrip: number
  vertStrips: number
  horizStrips: number
  vertInches: number
  horizInches: number
}

export function sashingPlan(project: Project): SashingStripPlan | null {
  const sw = project.layout.sashing_width
  if (sw <= 0) return null
  const { rows, cols } = project.layout
  if (rows <= 0 || cols <= 0) return null
  // On-point sashing geometry (rotated strips, bias edges) isn't supported;
  // the renderer skips sashing rects in the on-point branch, so the planner
  // must skip the cut/yardage too — billing for invisible strips would lie
  // about quilt content (LOOM_013 Facet B).
  if (project.layout.setting === "on_point") return null
  const sashFabric = findFabricByRole(project, "sashing")
  if (!sashFabric) return null

  const { seam: s } = project.block
  const [blockW, blockH] = blockFinishedInches(project.block)
  const sashCut = sw + 2 * s
  const cornerCut = sashCut
  const wof = sashFabric.width

  // Sashing strips run between blocks; their length matches the block edge
  // they border. Vertical strips (between columns in one row) span the block
  // height; horizontal strips (between rows in one column) span the block
  // width. For square blocks both lengths collapse to the same value.
  const vertPieceCut = blockH + 2 * s
  const horizPieceCut = blockW + 2 * s
  const vertCount = rows * (cols - 1)
  const horizCount = (rows - 1) * cols

  const vertPiecesPerStrip = Math.max(1, Math.floor(wof / vertPieceCut))
  const horizPiecesPerStrip = Math.max(1, Math.floor(wof / horizPieceCut))
  const vertStrips = vertCount > 0 ? Math.ceil(vertCount / vertPiecesPerStrip) : 0
  const horizStrips = horizCount > 0 ? Math.ceil(horizCount / horizPiecesPerStrip) : 0
  const vertInches = vertStrips * sashCut
  const horizInches = horizStrips * sashCut

  const sashCount = vertCount + horizCount
  const sashStrips = vertStrips + horizStrips
  const sashInches = vertInches + horizInches

  const hasCornerFabric = findFabricByRole(project, "cornerstone") !== null
  const cornerCount = hasCornerFabric ? 0 : (rows - 1) * (cols - 1)

  let cornerStrips = 0
  let cornerInches = 0
  if (cornerCount > 0) {
    const cpw = Math.max(1, Math.floor(wof / cornerCut))
    cornerStrips = Math.ceil(cornerCount / cpw)
    cornerInches = cornerStrips * cornerCut
  }

  return {
    sashCut,
    cornerCut,
    sashCount,
    cornerCount,
    piecesPerStrip: vertPiecesPerStrip,
    sashStrips,
    cornerStrips,
    sashInches,
    cornerInches,
    vertPieceCut, horizPieceCut,
    vertCount, horizCount,
    vertPiecesPerStrip, horizPiecesPerStrip,
    vertStrips, horizStrips,
    vertInches, horizInches,
  }
}

// Setting triangles fill the canvas-corner regions outside the inscribed
// lattice diamond under canonical on-point. Each is a right isoceles HST
// with legs along the canvas perimeter (kept on grain) and hypotenuse along
// a diamond edge.
//
// For a square M=N rotated lattice all four corner regions are congruent.
// For rectangular M≠N two pairs differ in size: top-left and bottom-right
// have legs M*blockFinished/√2; top-right and bottom-left have legs
// N*blockFinished/√2. v1 sizes the cut square to max(M, N) for all four —
// slight over-cut for the smaller pair (quilters trim after sewing).
// Future: precise per-corner cut sizes.
//
// On-point still requires square blocks; for non-square blocks the function
// returns null and the caller (yardage, plan, render) skips setting math.
//
// Note: existing fields `sideCutSquare`/`sideCount`/etc. are zeroed under
// this canonical layout — there are no per-block-edge side QSTs. Kept on
// the interface for backward-compatible serialization and incremental
// consumer migration.
export interface SettingTrianglePlan {
  blockFinished: number    // finished side of the (square) block in inches
  diagonal: number         // block diagonal = blockFinished * √2 (kept for reference)
  cornerLeg: number        // finished leg length of each corner triangle
  cornerCutSquare: number  // cut size: cornerLeg + 7/8" (HST 2-at-a-time)
  cornerCount: number      // 4 (always, when on-point)
  cornerSquares: number    // 2 (each cut on diagonal yields 2 HSTs → 4 total)
  cornerPerStrip: number   // floor(WOF / cornerCutSquare); 0 if oversized
  cornerStrips: number     // strips of width cornerCutSquare needed
  cornerInches: number     // WOF inches consumed by corner cut squares
  fitsWof: boolean         // false when cornerCutSquare exceeds fabric width — quilter must piece the corners
  // Legacy side fields, zero under canonical on-point.
  sideCutSquare: number
  sideCount: number
  sideSquares: number
  sidePerStrip: number
  sideStrips: number
  sideInches: number
}

export function settingTrianglePlan(project: Project): SettingTrianglePlan | null {
  if (project.layout.setting !== "on_point") return null
  const { rows, cols } = project.layout
  if (rows <= 0 || cols <= 0) return null
  const [blockW, blockH] = blockFinishedInches(project.block)
  if (blockW !== blockH) return null  // on-point restricted to square blocks
  const blockFinished = blockW
  // Setting fabric is the dedicated role; if absent fall back to background.
  const fabric = findFabricByRole(project, "setting") ?? findFabricBySlot(project, "background")
  if (!fabric) return null
  const wof = fabric.width
  const s = project.block.seam
  const seamScale = s / 0.25
  const diagonal = blockFinished * Math.SQRT2
  // Canonical Option A: 4 large corner setting triangles, sized by the
  // longer of the two lattice axes (max(rows, cols)). Each corner is a right
  // isoceles HST with legs along the canvas perimeter.
  const longerAxis = Math.max(rows, cols)
  const cornerLeg = longerAxis * blockFinished / Math.SQRT2
  const cornerCutSquare = roundUpEighth(cornerLeg + 0.875 * seamScale)
  const cornerCount = 4
  const cornerSquares = 2  // each yields 2 HSTs
  const cornerPerStrip = cornerCutSquare <= wof ? Math.max(1, Math.floor(wof / cornerCutSquare)) : 0
  const cornerStrips = cornerPerStrip > 0 ? Math.ceil(cornerSquares / cornerPerStrip) : cornerSquares
  const cornerInches = cornerStrips * cornerCutSquare
  const fitsWof = cornerCutSquare <= wof
  return {
    blockFinished, diagonal,
    cornerLeg, cornerCutSquare, cornerCount, cornerSquares,
    cornerPerStrip, cornerStrips, cornerInches, fitsWof,
    // Legacy fields — zero under canonical layout.
    sideCutSquare: 0, sideCount: 0, sideSquares: 0,
    sidePerStrip: 0, sideStrips: 0, sideInches: 0,
  }
}

// Border strips that frame the on-point pieced AABB into a rectangular
// finished blanket. v1 cuts butt-jointed borders: 2 side strips of
// (border_h_inches × AABB_height) and 2 top/bottom strips of
// (border_v_inches × finished_W). Quilters piece any strip whose length
// exceeds the fabric WOF.
export interface BorderPlan {
  borderH: number          // finished thickness on each of left/right (inches)
  borderV: number          // finished thickness on each of top/bottom (inches)
  aabbSide: number         // pieced AABB side in inches
  finishedW: number        // total finished width (AABB + 2*borderH)
  finishedH: number        // total finished height (AABB + 2*borderV)
  sideStripCount: number   // 2 when borderH > 0, else 0
  sideStripWidth: number   // = borderH (cut width)
  sideStripLength: number  // = aabbSide (cut length per strip)
  tbStripCount: number     // 2 when borderV > 0, else 0
  tbStripWidth: number     // = borderV
  tbStripLength: number    // = finishedW
  totalInches: number      // total fabric inches consumed (sum of strip rectangles ÷ WOF)
}

export function borderPlan(project: Project): BorderPlan | null {
  if (project.layout.setting !== "on_point") return null
  const { rows, cols } = project.layout
  if (rows <= 0 || cols <= 0) return null
  const [blockW, blockH] = blockFinishedInches(project.block)
  if (blockW !== blockH) return null
  const borderH = project.layout.border_h_inches ?? 0
  const borderV = project.layout.border_v_inches ?? 0
  if (borderH <= 0 && borderV <= 0) return null
  const fabric = findFabricByRole(project, "setting") ?? findFabricBySlot(project, "background")
  if (!fabric) return null
  const wof = fabric.width
  const aabbSide = (rows + cols) * blockW / Math.SQRT2
  const finishedW = aabbSide + 2 * borderH
  const finishedH = aabbSide + 2 * borderV
  const sideStripCount = borderH > 0 ? 2 : 0
  const tbStripCount = borderV > 0 ? 2 : 0
  // Total fabric area = sum of strip rectangles. Convert to WOF inches by
  // dividing by fabric width (assumes ideal cuts; quilters trim and piece).
  const sideArea = sideStripCount * borderH * aabbSide
  const tbArea = tbStripCount * borderV * finishedW
  const totalInches = (sideArea + tbArea) / wof
  return {
    borderH, borderV, aabbSide, finishedW, finishedH,
    sideStripCount, sideStripWidth: borderH, sideStripLength: aabbSide,
    tbStripCount, tbStripWidth: borderV, tbStripLength: finishedW,
    totalInches,
  }
}

// Cut sizes in quilting traditionally round up to the nearest 1/8". Keeps
// the cut plan reading the way a quilter expects.
function roundUpEighth(x: number): number {
  return Math.ceil(x * 8) / 8
}

export function cornerstonePlan(project: Project): SashingStripPlan | null {
  const sw = project.layout.sashing_width
  if (sw <= 0) return null
  const { rows, cols } = project.layout
  if (rows <= 1 || cols <= 1) return null
  // Cornerstones live at sashing intersections, which on-point doesn't paint
  // (see sashingPlan). Skip in lockstep so the cut tables match the render.
  if (project.layout.setting === "on_point") return null
  const csFabric = findFabricByRole(project, "cornerstone")
  if (!csFabric) return null

  const s = project.block.seam
  const cornerCut = sw + 2 * s
  const cornerCount = (rows - 1) * (cols - 1)
  const piecesPerStrip = Math.max(1, Math.floor(csFabric.width / cornerCut))
  const cornerStrips = Math.ceil(cornerCount / piecesPerStrip)
  const cornerInches = cornerStrips * cornerCut

  return {
    sashCut: cornerCut, cornerCut,
    sashCount: 0, cornerCount,
    piecesPerStrip, sashStrips: 0, cornerStrips,
    sashInches: 0, cornerInches,
    vertPieceCut: cornerCut, horizPieceCut: cornerCut,
    vertCount: 0, horizCount: 0,
    vertPiecesPerStrip: piecesPerStrip, horizPiecesPerStrip: piecesPerStrip,
    vertStrips: 0, horizStrips: 0,
    vertInches: 0, horizInches: 0,
  }
}

export function quiltFinishedInches(project: Project): [width: number, height: number] {
  const [blockW, blockH] = blockFinishedInches(project.block)
  const sashW = project.layout.sashing_width ?? 0
  const onPoint = project.layout.setting === "on_point" && blockW === blockH
  const straightH = project.layout.rows * blockH + (project.layout.rows > 1 ? (project.layout.rows - 1) * sashW : 0)
  const straightW = project.layout.cols * blockW + (project.layout.cols > 1 ? (project.layout.cols - 1) * sashW : 0)
  // Canonical on-point: the pieced AABB is a square — both axes equal
  // (rows+cols)*blockSide/√2 — and optional borders extend to a rectangular
  // finished blanket. Total finished dimension on each axis = AABB side +
  // 2 * border thickness on that axis.
  const aabbSide = (project.layout.rows + project.layout.cols) * blockH / Math.SQRT2
  const borderH = project.layout.border_h_inches ?? 0
  const borderV = project.layout.border_v_inches ?? 0
  const h = onPoint ? aabbSide + 2 * borderV : straightH
  const w = onPoint ? aabbSide + 2 * borderH : straightW
  return [w, h]
}

export interface SandwichPlan {
  quiltW: number
  quiltH: number
  bindingRunningInches: number
  bindingStripCount: number
  bindingFabricInches: number
  bindingYards: number
  backingWidthNeeded: number
  backingHeightNeeded: number
  backingPanels: 1 | 2
  backingFabricInches: number
  backingYards: number
  backingNote?: string
  battingWidthNeeded: number
  battingHeightNeeded: number
  battingSize: string | null
  battingW: number | null
  battingH: number | null
}

const BATTING_SIZES = [
  { name: "crib",  w: 45,  h: 60  },
  { name: "throw", w: 60,  h: 80  },
  { name: "twin",  w: 72,  h: 90  },
  { name: "full",  w: 84,  h: 96  },
  { name: "queen", w: 90,  h: 108 },
  { name: "king",  w: 120, h: 120 },
] as const

export function sandwichPlan(project: Project): SandwichPlan {
  const [quiltW, quiltH] = quiltFinishedInches(project)

  const bindingRunningInches = 2 * (quiltW + quiltH) * 1.1 + 12
  const bindingStripCount = Math.ceil(bindingRunningInches / FABRIC_WIDTH)
  const bindingFabricInches = bindingStripCount * 2.5
  const bindingYards = Math.ceil(bindingFabricInches / 36 * 8) / 8

  const backingWidthNeeded = quiltW + 8
  const backingHeightNeeded = quiltH + 8
  const backingPanels: 1 | 2 = backingWidthNeeded <= FABRIC_WIDTH ? 1 : 2
  const backingFabricInches = backingPanels * backingHeightNeeded
  const backingYards = Math.ceil(backingFabricInches / 36 * 8) / 8
  const backingNote = backingPanels === 2
    ? `Piece 2 panels of ${Math.ceil(backingHeightNeeded)}" each; seam runs parallel to the long edge.`
    : undefined

  const battingWidthNeeded = quiltW + 8
  const battingHeightNeeded = quiltH + 8
  const fit = BATTING_SIZES.find((s) =>
    (s.w >= battingWidthNeeded && s.h >= battingHeightNeeded) ||
    (s.w >= battingHeightNeeded && s.h >= battingWidthNeeded)
  ) ?? null
  const battingRotated = fit !== null && !(fit.w >= battingWidthNeeded && fit.h >= battingHeightNeeded)
  const battingW = battingRotated ? fit!.h : (fit?.w ?? null)
  const battingH = battingRotated ? fit!.w : (fit?.h ?? null)

  return {
    quiltW, quiltH,
    bindingRunningInches, bindingStripCount, bindingFabricInches, bindingYards,
    backingWidthNeeded, backingHeightNeeded, backingPanels,
    backingFabricInches, backingYards, backingNote,
    battingWidthNeeded, battingHeightNeeded,
    battingSize: fit ? `${fit.name} (${battingW}×${battingH})` : null,
    battingW, battingH,
  }
}
